import React, { useEffect, useState, useCallback, useContext, useRef } from "react";
import { useParams } from "react-router-dom";
import { Box, Button, Stack, TextField, InputLabel, Typography, Divider, Switch } from "@mui/material";
import { addOrUpdateSpeaker, deleteSpeaker, getSpeakerById } from "../../Requests/SpeakerService.ts";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { SpeakersStoreContext } from "../../Store/SpeakersStore";
import AvatarDropzone from "../StyledComponents/AvatarDropzone/AvatarDropzone";
import { DeleteForever, ArrowBack, FileDownload } from "@mui/icons-material";
import { exportDataToCsv } from "../../Utils/exportTableToCsv.js";
import { Speaker } from "../../Models/Speaker.ts";
import CircularProgressView from "../StyledComponents/CircularProgressView.jsx";
import HtmlEditor from '../HtmlEditorView/HtmlEditor.jsx';


export default function SpeakersInfo() {

  const navigate = useNavigate()
  const { speakerid } = useParams()
  const store = useContext(SpeakersStoreContext)
  const [isLoading, setIsLoading] = useState(true)

  const [avatar, setAvatar] = useState()
  const [firstName, setFirstName] = useState()
  const [lastName, setLastName] = useState()
  const [bio, setBio] = useState()
  const [hidden, setHidden] = useState()
  const [createdAt, setCreatedAt] = useState()
  const [updatedAt, setUpdatedAt] = useState()
  const [occupation, setOccupation] = useState()
  const [sortOrder, setSortOrder] = useState()

  const headerRef = useRef(null)
  const APP_BAR_HEIGHT = 64
  const [headerHeight, setHeaderHeight] = useState(0)

  useEffect(() => {
    if (!headerRef.current) return

    const updateHeight = () => {
      setHeaderHeight(headerRef.current?.offsetHeight ?? 0)
    }

    updateHeight()

    const observer = new ResizeObserver(updateHeight)
    observer.observe(headerRef.current)

    return () => observer.disconnect()
  }, [])

  const HEADER_BOTTOM_OFFSET = APP_BAR_HEIGHT + headerHeight

  const fetchData = useCallback(async () => {
    const result = await getSpeakerById(speakerid)
    setAvatar(result.avatar)
    setFirstName(result.firstName)
    setLastName(result.lastName)
    setBio(result.bio)
    setHidden(result.hidden)
    setCreatedAt(result.createdAt)
    setUpdatedAt(result.updatedAt)
    setOccupation(result.occupation)
    setSortOrder(result.sortOrder)

    setIsLoading(false)
  }, [speakerid])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleSaveChanges = async () => {
    let speaker = new Speaker()
    speaker.objectId = speakerid
    speaker.firstName = firstName
    speaker.lastName = lastName
    speaker.bio = bio
    speaker.hidden = hidden
    speaker.occupation = occupation
    speaker.sortOrder = sortOrder
    await addOrUpdateSpeaker(speaker, avatar)
    // Mark store as needing refresh
    store.needsRefresh = true
    navigate("/speakers")
  }

  const handleHiddenSpeaker = async (event) => {
    setHidden(!event)
  }

  const handleDeleteSpeaker = async () => {
    await deleteSpeaker(speakerid)
    // Mark store as needing refresh
    store.needsRefresh = true
    navigate("/speakers")
  }

  const getSpeakerForExport = () => ({
    objectId: speakerid,
    avatar,
    firstName,
    lastName,
    bio,
    hidden,
    createdAt,
    updatedAt,
    occupation,
    sortOrder,
  })

  const loaderView = <CircularProgressView />

  const contentView = <>
    <Box
      ref={headerRef}
      sx={{
        position: "sticky",
        top: APP_BAR_HEIGHT,
        backgroundColor: "background.paper",
        zIndex: 100
      }}
    >
      <Box
        paddingTop={2}
        paddingBottom={2}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "start",
          gap: 1,
          width: "calc(100% - 280px)",
          paddingRight: 2
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate("/speakers")}
            sx={{ minWidth: "auto", alignSelf: "flex-start" }}
          >
            Back
          </Button>
          <Typography
            variant="h4"
            sx={{
              maxWidth: "600px"
            }}
          >
            {firstName + " " + lastName}
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          onClick={() => handleSaveChanges()}
        >
          Save Changes
        </Button>
      </Box>
      <Divider />
    </Box>


    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between"
      }}
    >
      <Box
        width="calc(100% - 280px)"
        sx={{
          paddingRight: 2
        }}
      >
        <AvatarDropzone
          title={"Speaker Avatar"}
          avatarUrl={avatar}
          callbackFile={(file) => { setAvatar(file) }}
        />

        <Stack
          spacing={1}
          marginTop={4}
        >
          <InputLabel>{"First Name"}</InputLabel>
          <TextField
            placeholder="First Name"
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
          />
        </Stack>

        <Stack
          spacing={1}
          marginTop={4}
        >
          <InputLabel>{"Last Name"}</InputLabel>
          <TextField
            placeholder="Last Name"
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
          />
        </Stack>

        <Stack
          spacing={1}
          marginTop={4}
        >
          <InputLabel>{"Occupation"}</InputLabel>
          <TextField
            placeholder="Occupation"
            value={occupation}
            onChange={(event) => setOccupation(event.target.value)}
          />
        </Stack>

        <Stack
          spacing={1}
          marginTop={4}
        >
          <InputLabel>{"Bio"}</InputLabel>
          <HtmlEditor initialValue={bio} onChange={setBio} />
        </Stack>

        <Stack
          spacing={1}
          marginTop={4}
        >
          <InputLabel>{"Sort order"}</InputLabel>
          <TextField
            type="number"
            placeholder="Enter speaker position"
            value={sortOrder}
            onChange={(event) => setSortOrder(Number(event.target.value))}
          />
        </Stack>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          width: "280px",
          position: "fixed",
          right: 0,
          top: APP_BAR_HEIGHT + 1,
          bottom: 0,
          overflowY: "auto",
          backgroundColor: "background.paper",
          borderLeft: "1px solid",
          borderColor: "grey.200",
          zIndex: 110
        }}
      >
        <Divider orientation="vertical" />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            padding: 2
          }}
        >
          <Button
            size="small"
            startIcon={<FileDownload />}
            onClick={() =>
              exportDataToCsv([getSpeakerForExport()], speakerid ? `speaker-${speakerid}` : "speaker", {
                preferredKeys: ["firstName", "lastName"],
              })
            }
            sx={{ mb: 2, alignSelf: "flex-start" }}
          >
            Export CSV
          </Button>
          <Box
            width="100%"
            display="flex"
            justifyContent="space-between"
          >
            <InputLabel>{"Date Created"}</InputLabel>
            <InputLabel>{dayjs(createdAt).format('DD/MMM/YYYY')}</InputLabel>
          </Box>

          <Box
            marginTop={1}
            width="100%"
            display="flex"
            justifyContent="space-between"
          >
            <InputLabel>{"Last Update"}</InputLabel>
            <InputLabel>{dayjs(updatedAt).format('DD/MMM/YYYY')}</InputLabel>
          </Box>

          <Box
            marginTop={1}
            marginBottom={2}
            width="100%"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <InputLabel>{"Visibility"}</InputLabel>
            <Switch
              checked={!hidden}
              onChange={(event) => handleHiddenSpeaker(event.target.checked)}
            />
          </Box>

          <Divider />

          <Box
            sx={{
              marginTop: 2,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 1
            }}
          >
            <Button
              variant="outlined"
              color="error"
              onClick={() => handleDeleteSpeaker()}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 1
                }}
              >
                <DeleteForever />
                Delete Speaker
              </Box>
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  </>

  return (
    <>
      {isLoading ? loaderView : contentView}
    </>
  )
}
